import { useEffect, useState, useRef } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * Exploded Component Compliance View
 * Scroll-driven compliance verification of fire safety components
 * based on scroll.md Pattern 2: "Exploded View Strategy"
 */

interface Component {
    id: string;
    name: string;
    description: string;
    color: string;
    zIndex: number;
}

const DETECTOR_COMPONENTS: Component[] = [
    {
        id: 'mounting',
        name: 'Mounting Bracket',
        description: 'Universal ceiling mount with quick-release mechanism',
        color: '#555',
        zIndex: 1,
    },
    {
        id: 'casing',
        name: 'Protective Casing',
        description: 'Fire-resistant ABS polymer housing',
        color: '#f5f5f5',
        zIndex: 2,
    },
    {
        id: 'circuit',
        name: 'Processing Unit',
        description: 'AI-powered sensor processing board',
        color: '#2d5016',
        zIndex: 3,
    },
    {
        id: 'sensor',
        name: 'Multi-Sensor Chamber',
        description: 'Photoelectric + ionization + heat detection',
        color: '#ff6b35',
        zIndex: 4,
    },
];

export function EquipmentSection() {
    const { ref, isInView } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });
    const reducedMotion = useReducedMotion();
    const sectionRef = useRef<HTMLDivElement>(null);
    const [explosionProgress, setExplosionProgress] = useState(0);

    useEffect(() => {
        if (reducedMotion) return;

        const handleScroll = () => {
            if (!sectionRef.current) return;

            const rect = sectionRef.current.getBoundingClientRect();
            const sectionTop = rect.top;
            const sectionHeight = rect.height;
            const viewportHeight = window.innerHeight;

            // Calculate how far through the section we've scrolled
            // 0 = section just entering viewport from bottom
            // 1 = section just leaving viewport from top
            const scrollProgress = Math.max(
                0,
                Math.min(
                    1,
                    (viewportHeight - sectionTop) / (viewportHeight + sectionHeight)
                )
            );

            // Map to explosion progress (0 = together, 1 = fully exploded)
            // Explode during middle 60% of scroll through section
            const explosionStart = 0.2;
            const explosionEnd = 0.8;

            if (scrollProgress < explosionStart) {
                setExplosionProgress(0);
            } else if (scrollProgress > explosionEnd) {
                setExplosionProgress(1);
            } else {
                const normalizedProgress =
                    (scrollProgress - explosionStart) / (explosionEnd - explosionStart);
                setExplosionProgress(normalizedProgress);
            }
        };

        handleScroll(); // Initial calculation
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, [reducedMotion]);

    // Calculate component offsets based on explosion progress
    const getComponentTransform = (index: number) => {
        if (reducedMotion) return {};

        // Each component moves up more than the one below it
        const baseOffset = index * 80; // Base spacing
        const offset = baseOffset * explosionProgress;
        const rotation = index * 5 * explosionProgress; // Slight rotation for depth

        return {
            transform: `translateY(-${offset}px) rotateZ(${rotation}deg)`,
            transition: 'transform 0.1s linear',
        };
    };

    return (
        <section
            ref={ref}
            id="equipment-section"
            className="py-24 px-4 md:px-8 lg:px-16 relative overflow-hidden"
            style={{ background: 'var(--dark-bg)', minHeight: '120vh' }}
            aria-labelledby="equipment-title"
        >
            <div className="max-w-7xl mx-auto" ref={sectionRef}>
                <div className="grid lg:grid-cols-2 gap-16 items-start">
                    {/* Left: Component List */}
                    <div className="space-y-8">
                        <div>
                            <h2
                                id="equipment-title"
                                className="text-4xl md:text-5xl font-display font-bold text-white mb-4"
                            >
                                Component-Level <br />
                                <span className="text-[var(--fire-orange)]">Compliance Verification</span>
                            </h2>
                            <p className="text-gray-400 text-lg">
                                We analyze every component of your specified hardware against rigorous safety protocols.
                                Our reports certify the integrity of the entire assembly.
                            </p>
                        </div>

                        {/* Component Details */}
                        <div className="space-y-6">
                            {DETECTOR_COMPONENTS.map((component, index) => (
                                <div
                                    key={component.id}
                                    className="component-card p-5 rounded-xl border transition-all duration-300"
                                    style={{
                                        borderColor:
                                            explosionProgress > index / DETECTOR_COMPONENTS.length
                                                ? 'var(--neon-cyan)'
                                                : 'var(--blueprint-line)',
                                        background:
                                            explosionProgress > index / DETECTOR_COMPONENTS.length
                                                ? 'rgba(0, 212, 255, 0.05)'
                                                : 'transparent',
                                    }}
                                >
                                    <div className="flex items-start gap-4">
                                        <div
                                            className="w-3 h-3 rounded-full mt-1.5"
                                            style={{
                                                background: component.color,
                                                boxShadow:
                                                    explosionProgress > index / DETECTOR_COMPONENTS.length
                                                        ? `0 0 8px ${component.color}`
                                                        : 'none',
                                            }}
                                        />
                                        <div className="flex-1">
                                            <h3 className="font-display font-bold text-white mb-1">
                                                {component.name}
                                            </h3>
                                            <p className="text-gray-400 text-sm">
                                                {component.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Technical Specs */}
                        <div className="p-6 rounded-xl border border-[var(--blueprint-line)] bg-[var(--card-bg)]">
                            <h4 className="font-mono text-xs uppercase tracking-wider text-[var(--neon-cyan)] mb-4">
                                Technical Specifications
                            </h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <div className="text-gray-500">Operating Temp</div>
                                    <div className="text-white font-mono">-10°C to 50°C</div>
                                </div>
                                <div>
                                    <div className="text-gray-500">Response Time</div>
                                    <div className="text-white font-mono">&lt; 500ms</div>
                                </div>
                                <div>
                                    <div className="text-gray-500">Coverage Area</div>
                                    <div className="text-white font-mono">400 sq ft</div>
                                </div>
                                <div>
                                    <div className="text-gray-500">Power</div>
                                    <div className="text-white font-mono">24V DC / Battery</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Exploded View Visual */}
                    <div className="relative flex items-center justify-center min-h-[600px] lg:sticky lg:top-24">
                        <div className="relative w-full max-w-md aspect-square">
                            {/* Mounting Bracket (Bottom) */}
                            <div
                                className="absolute inset-0 flex items-center justify-center"
                                style={{ ...getComponentTransform(0), zIndex: DETECTOR_COMPONENTS[0].zIndex }}
                            >
                                <div
                                    className="w-48 h-12 rounded-lg"
                                    style={{
                                        background: `linear-gradient(135deg, ${DETECTOR_COMPONENTS[0].color}, #333)`,
                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                                    }}
                                >
                                    <div className="w-full h-full border-2 border-gray-600 rounded-lg flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full border-2 border-gray-400" />
                                    </div>
                                </div>
                            </div>

                            {/* Protective Casing */}
                            <div
                                className="absolute inset-0 flex items-center justify-center"
                                style={{ ...getComponentTransform(1), zIndex: DETECTOR_COMPONENTS[1].zIndex }}
                            >
                                <div
                                    className="w-56 h-16 rounded-2xl"
                                    style={{
                                        background: `linear-gradient(135deg, ${DETECTOR_COMPONENTS[1].color}, #ddd)`,
                                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                                    }}
                                />
                            </div>

                            {/* Circuit Board */}
                            <div
                                className="absolute inset-0 flex items-center justify-center"
                                style={{ ...getComponentTransform(2), zIndex: DETECTOR_COMPONENTS[2].zIndex }}
                            >
                                <div
                                    className="w-44 h-44 rounded-xl p-4"
                                    style={{
                                        background: DETECTOR_COMPONENTS[2].color,
                                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
                                    }}
                                >
                                    {/* Circuit traces */}
                                    <svg className="w-full h-full opacity-70">
                                        <line
                                            x1="20%"
                                            y1="50%"
                                            x2="80%"
                                            y2="50%"
                                            stroke="#888"
                                            strokeWidth="2"
                                        />
                                        <line
                                            x1="50%"
                                            y1="20%"
                                            x2="50%"
                                            y2="80%"
                                            stroke="#888"
                                            strokeWidth="2"
                                        />
                                        <circle cx="50%" cy="50%" r="10" fill="#ffb800" />
                                        <circle cx="30%" cy="30%" r="5" fill="#00d4ff" />
                                        <circle cx="70%" cy="30%" r="5" fill="#00d4ff" />
                                        <circle cx="30%" cy="70%" r="5" fill="#00d4ff" />
                                        <circle cx="70%" cy="70%" r="5" fill="#00d4ff" />
                                    </svg>
                                </div>
                            </div>

                            {/* Sensor Chamber (Top) */}
                            <div
                                className="absolute inset-0 flex items-center justify-center"
                                style={{ ...getComponentTransform(3), zIndex: DETECTOR_COMPONENTS[3].zIndex }}
                            >
                                <div
                                    className="w-32 h-32 rounded-full flex items-center justify-center"
                                    style={{
                                        background: `linear-gradient(135deg, ${DETECTOR_COMPONENTS[3].color}, #ff3b00)`,
                                        boxShadow: `0 0 40px ${DETECTOR_COMPONENTS[3].color}, 0 10px 50px rgba(0, 0, 0, 0.5)`,
                                    }}
                                >
                                    <div className="w-20 h-20 rounded-full bg-black/30 flex items-center justify-center">
                                        <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
                                    </div>
                                </div>
                            </div>

                            {/* Scroll Indicator */}
                            <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-center">
                                <div className="text-sm text-gray-500 font-mono">
                                    SCROLL TO VERIFY
                                </div>
                                <div className="w-32 h-1 bg-gray-800 rounded-full mt-2 overflow-hidden">
                                    <div
                                        className="h-full bg-[var(--neon-cyan)] transition-all duration-100"
                                        style={{ width: `${explosionProgress * 100}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default EquipmentSection;
